import React, { useRef, useState, useEffect } from "react";
import { Button, TextField } from "@mui/material";
import {
  TranslationEnum,
  usePortalTranslation,
} from "../../providers/TranslationProvider";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/Buttons/BackButton/BackButton";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import "./ManageForm.css";
import { manageUpdateAccessObj } from "../manageUpdateAccess/manageUpdateAccessTypes";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { getLob } from "../../services/authenticationService";
import {
  ChannelkartNetworkGet,
  META_DATA_BATCH_API,
} from "../../services/networkService";
import { validateMetaDataResponse } from "../../services/uiConfigService";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { ConfirmationPopUp } from "../../components/popup/confirmationPopUp";
import { metaDataBatchPayload, popupType } from "../../types/types";

interface FormBoardProps {
  hideBackButton?: boolean;
}

export default function ManageForm(props: FormBoardProps) {
  const [searchText, setSearchText] = useState<string>("");
  const [rows, setRows] = useState<any[]>([]);
  const { translate } = usePortalTranslation();
  const [formData, setFormData] = useState<any[]>([]);
  const [confirmPopupMessage, setConfirmPopupMessage] = useState<string>("");
  const [openConfirmPopup, setOpenConfirmPopup] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(true);
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const selectedFormId = useRef<string>("");

  const allFormsRef = useRef<any[]>([]);

  useEffect(() => {
    const fetchForms = async () => {
      const url = "/metadata/clientconfig?name=form_configuration";
      try {
        const response = await ChannelkartNetworkGet(url);
        setFormData(response.data.features[0].domainValues);
        allFormsRef.current = response.data.features[0].domainValues[0].value;
        console.log("All Forms:", response.data.features[0].domainValues);
        setRows(allFormsRef.current);
        console.log("Form Config:", allFormsRef.current);
      } catch (err) {
        console.log(err);
      }
    };

    fetchForms();
    localStorage.removeItem("formState");
    setLoader(false);
    return () => {
      localStorage.removeItem("formState");
    };
  }, []);

  useEffect(() => {
    localStorage.removeItem("formState");
    return () => {
      localStorage.removeItem("formState");
    };
  }, []);

  const domainValues = useRef<any[]>([]);
  const manageFormSearchParams = ["formName", "api"];

  let columns: GridColDef[] = [
    { field: "formId", headerName: "ID", width: 350 },
    { field: "formName", headerName: "Form Name", width: 240 },
    { field: "api", headerName: "API", width: 230 },
  ];

  const addActionAttributes = (
    field: string,
    headerName: string,
    action: (params: GridRenderCellParams) => JSX.Element | null
  ) => {
    columns.push({
      field,
      headerName,
      sortable: false,
      minWidth: 70,
      flex: 0.5,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderCell: action,
    });
  };

  addActionAttributes("Update", "", (params: GridRenderCellParams) => {
    return (
      <button
        className="manage-form-table-button"
        onClick={() => {
          navigate("createForm", {
            state: {
              row: params.row,
              domainValues: domainValues.current,
              initialForm: {
                api: params.row.api,
                sections: params.row.sections,
                formName: params.row.formName,
                buttonLabel: params.row.buttonLabel,
                formId: params.row.formId,
              },
            },
          });
        }}
      >
        {translate(TranslationEnum.common_portal, "UPDATE")}
      </button>
    );
  });

  addActionAttributes("Delete", "", (params: GridRenderCellParams) => {
    return (
      <button
        className="manage-form-table-button"
        onClick={() => {
          handleDelete(params?.row?.formId);
        }}
      >
        {translate(TranslationEnum.common_portal, "DELETE")}
      </button>
    );
  });

  const handleDelete = (id: string) => {
    selectedFormId.current = id;
    setConfirmPopupMessage(
      translate(TranslationEnum.manage_bucket, `Do you want to delete bucket`)
    );
    setOpenConfirmPopup(true);
  };

  const deleteForm = (id: string) => {
    const formConfig = allFormsRef.current.filter((f) => f.formId !== id);
    formData[0].value = formConfig;
    const finalMetaDataPayload: metaDataBatchPayload = { features: [] };
    let formPayload = {
      domainName: "clientconfig",
      domainType: "form_configuration",
      domainValues: formData,
      lob: getLob(),
    };
    finalMetaDataPayload.features.push(formPayload);
    try{
      validateWithOtp(
        (verifiedOTPUser?: manageUpdateAccessObj, verifyResponse?: any) => {
          doFinalSubmit(verifiedOTPUser, verifyResponse, id);
        },
        undefined,
        "all",
        META_DATA_BATCH_API,
        finalMetaDataPayload,
        "PUT"
      );
    }catch(err){
      console.log(err);
    }
  };

  const doFinalSubmit = async (
    verifiedUser?: manageUpdateAccessObj,
    verifyResponse?: any,
    id?: string
  ) => {
    if (verifyResponse.status === 200 || verifyResponse.status === 201) {
      const validationObj = validateMetaDataResponse(
        verifyResponse?.data?.features
      );
      if (validationObj.success) {
        setPopupMessage(`deleted successfully`);
        setPopupType("Success");
        setOpenGenericModal(true);
        const updatedRows = rows.filter((row) => row.formId !== id);
        setRows(updatedRows);
        navigate("/dashboard/manageForms");
      } else {
        setPopupMessage(validationObj.message);
        setPopupType("Error");
        setOpenGenericModal(true);
      }
    } else {
      throw new Error();
    }
  };

  const defaultFilter = (value: any, search: string) => {
    return value?.toString().toLowerCase().includes(search.toLowerCase());
  };

  const filterFormData = (search: string) => {
    if (search) {
      const newData = allFormsRef.current.filter((formObj) =>
        manageFormSearchParams.some((param) =>
          defaultFilter(formObj[param], search)
        )
      );
      setRows(newData);
    } else {
      setRows(allFormsRef.current);
    }
  };

  useEffect(() => {
    setRows(allFormsRef.current);
  }, []);

  return (
    <>
      <div className="manage-form-parent">
        <div className="manage-form-header">
          <div className="manage-form-label">
            <span>
              {translate(TranslationEnum.manage_form, "Manage Form")}
            </span>
          </div>
          <div className="manage-form-buttons">
            <TextField
              label={translate(TranslationEnum.common_portal, "Search")}
              name="manageFormSearch"
              value={searchText}
              size="small"
              onChange={(event) => {
                setSearchText(event.target.value);
                filterFormData(event.target.value);
              }}
            />
            <Button
              className="manage-form-create-new"
              onClick={() => {
                navigate("createForm", {
                  state: {
                    row: { formId: "", formName: "", api: "" },
                    domainValues: [],
                    initialForm: null,
                  },
                });
              }}
              style={{
                backgroundColor: "#52c41a",
                color: "#fff",
                padding: "6px 12px",
                borderRadius: "4px",
                marginLeft: "8px",
              }}
            >
              {translate(
                TranslationEnum.create_form,
                "Create Form"
              ).toUpperCase()}
            </Button>
            {!props.hideBackButton && <BackButton />}
          </div>
        </div>
        {loader ? (
          <div>Loading...</div>
        ) : (
          <div className="manage-form-table">
            <DataGrid
              getRowId={(row) => row.formId}
              disableRowSelectionOnClick
              sx={{
                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                  outline: "none !important",
                },
              }}
              rows={rows}
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: "formId", sort: "asc" }],
                },
              }}
            />
          </div>
        )}
      </div>

      <ConfirmationPopUp
        openConfirmModal={openConfirmPopup}
        message={confirmPopupMessage}
        setOpenConfirmModal={() => {
          setOpenConfirmPopup(false);
        }}
        successMethod={() => {
          deleteForm(selectedFormId.current);
        }}
      />

      <GenericPopUp
        type={popupType}
        message={popupMessage}
        setOpenGenericModal={
          popupType === "Success"
            ? () => {
                setOpenGenericModal(false);
                window.history.back();
              }
            : setOpenGenericModal
        }
        openGenericModal={openGenericModal}
      />
    </>
  );
}
